/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import cx from 'classnames';
import Carousel from '@/components/Carousel';
import NextImage, { blurDataURLPlaceholder } from '@/components/NextImage';
import { NEXT, PREV } from '@/components/Carousel/constants';
import type { ThumbnailProps, CarouselItem } from '@/components/Carousel/ThumbnailComponent/types';
import type { CarouselProps, Direction } from '@/components/Carousel/types';
import { type ProductReviewImage } from '../../../../interface';
import styles from './styles.module.scss';
import { useLabels } from '@/hooks/useLabels';

export type ReviewImageCarouselProps = {
  images: ProductReviewImage[];
  selectedThumbnailIndex: CarouselProps['selectedThumbnailIndex'];
  slideAnimationDirection?: Direction;
  onSlide: CarouselProps['onSlide'];
  onImageChange: ThumbnailProps['onImageChange'];
};

const normalizeCarouselItems = (
  images: ProductReviewImage[],
  altFallback: string
): CarouselItem[] => {
  return images.map((image) => {
    return {
      alt: image.photoCaption ?? altFallback,
      type: 'image',
      thumbnail: image.normalPhotoUrl,
      mediumImageURL: image.normalPhotoUrl,
      enlargedMediaUrl: image.normalPhotoUrl,
    } as const;
  });
};

const labelMap = {
  customerImageAltFallback: 'label_reviews_customerImageAltFallback',
};

export const ReviewImageCarousel = ({
  images,
  selectedThumbnailIndex,
  slideAnimationDirection,
  onSlide,
  onImageChange,
}: ReviewImageCarouselProps) => {
  const labels = useLabels(labelMap);
  const thumbnails = normalizeCarouselItems(images, labels.customerImageAltFallback);

  return (
    <Carousel
      showArrows
      controlled
      items={thumbnails}
      selectedThumbnailIndex={selectedThumbnailIndex}
      onSlide={onSlide}
      onImageChange={onImageChange}
      classes={{
        root: styles.carouselRoot,
        customCarouselContainer: styles.customCarouselContainer,
        carouselControl: styles.carouselArrowButtons,
        carouselItemContainer: styles.carouselImageWrapper,
      }}
      thumbnailClasses={{
        thumbnailContainer: styles.thumbnailContainer,
        thumbnailList: styles.reviewThumbnailList,
      }}
      leftArrowImage="/images/left-arrow-outlined.svg"
      rightArrowImage="/images/right-arrow-outlined.svg"
    >
      {images.map((image) => (
        <NextImage
          key={image.id}
          src={image.normalPhotoUrl}
          className={cx(styles.carouselImage, {
            [styles.slideLeft]: slideAnimationDirection === NEXT,
            [styles.slideRight]: slideAnimationDirection === PREV,
          })}
          width={460}
          height={460}
          blurDataURL={blurDataURLPlaceholder}
          alt={image.photoCaption ?? labels.customerImageAltFallback}
        />
      ))}
    </Carousel>
  );
};
